import { useState, useRef, useCallback } from 'react';
import { API } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Camera, X, RotateCcw, Check, Loader2, ShieldCheck } from 'lucide-react';

export default function SelfieVerificationModal({ onClose, onSubmitted }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [step, setStep] = useState('intro'); // intro | camera | preview | submitting | done
  const [capturedImage, setCapturedImage] = useState(null);
  const [cameraError, setCameraError] = useState(null);

  const startCamera = useCallback(async () => {
    setCameraError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
      setStep('camera');
    } catch (err) {
      setCameraError('Camera access denied. Please allow camera permissions and try again.');
      console.error('Camera error:', err);
    }
  }, []);

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  const capturePhoto = useCallback(() => {
    if (!videoRef.current || !canvasRef.current) return;
    const video = videoRef.current;
    const canvas = canvasRef.current;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    // Mirror the image (selfie mode)
    ctx.translate(canvas.width, 0);
    ctx.scale(-1, 1);
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
    setCapturedImage(dataUrl);
    stopCamera();
    setStep('preview');
  }, [stopCamera]);

  const retake = useCallback(() => {
    setCapturedImage(null);
    startCamera();
  }, [startCamera]);

  const submitSelfie = async () => {
    if (!capturedImage) return;
    setStep('submitting');
    try {
      await axios.post(`${API}/auth/selfie-verify`, {
        selfie_data: capturedImage,
      });
      setStep('done');
      toast.success('Selfie submitted for review');
      setTimeout(() => {
        onSubmitted();
      }, 2000);
    } catch (err) {
      toast.error('Failed to submit selfie. Please try again.');
      setStep('preview');
    }
  };

  const handleClose = () => {
    stopCamera();
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center" data-testid="selfie-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={handleClose} />
      <div className="relative bg-white rounded-3xl shadow-2xl w-[90%] max-w-sm overflow-hidden animate-scale-in" data-testid="selfie-modal-card">
        {/* Header */}
        <div className="flex items-center justify-between p-5 pb-0">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-xl bg-amber-100 flex items-center justify-center">
              <ShieldCheck className="h-4 w-4 text-amber-600" />
            </div>
            <h3 className="text-sm font-bold text-[#0A2540]">Identity Verification</h3>
          </div>
          <button onClick={handleClose} className="w-7 h-7 rounded-full bg-slate-100 flex items-center justify-center hover:bg-slate-200" data-testid="selfie-close-btn">
            <X className="h-3.5 w-3.5 text-slate-500" />
          </button>
        </div>

        {/* Intro Step */}
        {step === 'intro' && (
          <div className="p-5 space-y-4" data-testid="selfie-intro">
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
              <p className="text-xs text-amber-700 leading-relaxed">
                Our system has detected unusual activity on your account. To continue, please verify your identity with a quick selfie.
              </p>
            </div>
            <div className="space-y-2.5">
              <Guideline text="Make sure your face is clearly visible" />
              <Guideline text="Remove sunglasses or hats" />
              <Guideline text="Good lighting helps — face a window if possible" />
            </div>
            {cameraError && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3">
                <p className="text-[11px] text-red-600">{cameraError}</p>
              </div>
            )}
            <button
              onClick={startCamera}
              className="w-full py-3.5 rounded-2xl bg-amber-500 text-white font-bold text-sm hover:bg-amber-600 active:scale-[0.98] transition-all shadow-lg shadow-amber-200 flex items-center justify-center gap-2"
              data-testid="selfie-start-camera-btn"
            >
              <Camera className="h-4 w-4" /> Open Camera
            </button>
          </div>
        )}

        {/* Camera Step */}
        {step === 'camera' && (
          <div className="p-5 space-y-4" data-testid="selfie-camera-view">
            <div className="relative rounded-2xl overflow-hidden bg-black aspect-[4/3]">
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="w-full h-full object-cover"
                style={{ transform: 'scaleX(-1)' }}
              />
              {/* Face guide overlay */}
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-40 h-52 rounded-[50%] border-2 border-white/40 border-dashed" />
              </div>
            </div>
            <button
              onClick={capturePhoto}
              className="w-full py-3.5 rounded-2xl bg-[#2F80ED] text-white font-bold text-sm hover:bg-[#1A6BD4] active:scale-[0.98] transition-all shadow-lg shadow-blue-200 flex items-center justify-center gap-2"
              data-testid="selfie-capture-btn"
            >
              <Camera className="h-4 w-4" /> Take Photo
            </button>
          </div>
        )}

        {/* Preview Step */}
        {step === 'preview' && capturedImage && (
          <div className="p-5 space-y-4" data-testid="selfie-preview-view">
            <div className="relative rounded-2xl overflow-hidden bg-black aspect-[4/3]">
              <img src={capturedImage} alt="Selfie preview" className="w-full h-full object-cover" />
            </div>
            <div className="flex gap-3">
              <button
                onClick={retake}
                className="flex-1 py-3 rounded-2xl bg-slate-100 text-slate-700 font-bold text-sm hover:bg-slate-200 transition-all flex items-center justify-center gap-2"
                data-testid="selfie-retake-btn"
              >
                <RotateCcw className="h-4 w-4" /> Retake
              </button>
              <button
                onClick={submitSelfie}
                className="flex-1 py-3 rounded-2xl bg-emerald-500 text-white font-bold text-sm hover:bg-emerald-600 active:scale-[0.98] transition-all shadow-lg shadow-emerald-200 flex items-center justify-center gap-2"
                data-testid="selfie-submit-btn"
              >
                <Check className="h-4 w-4" /> Submit
              </button>
            </div>
          </div>
        )}

        {/* Submitting Step */}
        {step === 'submitting' && (
          <div className="p-5 py-10 flex flex-col items-center gap-3" data-testid="selfie-submitting">
            <Loader2 className="h-8 w-8 animate-spin text-amber-500" />
            <p className="text-sm font-bold text-slate-700">Submitting verification...</p>
          </div>
        )}

        {/* Done Step */}
        {step === 'done' && (
          <div className="p-5 py-10 flex flex-col items-center gap-3" data-testid="selfie-done">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <Check className="h-7 w-7 text-emerald-600" />
            </div>
            <p className="text-sm font-bold text-slate-700">Selfie Submitted</p>
            <p className="text-xs text-slate-400 text-center">Our team will review and verify your identity shortly.</p>
          </div>
        )}

        <canvas ref={canvasRef} className="hidden" />

        <style>{`
          @keyframes scaleIn {
            0% { transform: scale(0.9); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
          }
          .animate-scale-in { animation: scaleIn 0.25s ease-out; }
        `}</style>
      </div>
    </div>
  );
}

function Guideline({ text }) {
  return (
    <div className="flex items-center gap-2.5">
      <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center shrink-0">
        <Check className="h-3 w-3 text-emerald-600" />
      </div>
      <span className="text-xs text-slate-600">{text}</span>
    </div>
  );
}
